import type { TravelMode } from '@london-life/contracts';

// The pure parts of a ride: how far along the street the player is at each moment (pulling away, the steady
// run, slowing to a stop at the place), and the areas the way passes through ("Now passing: Canary Wharf").

// a ride: one of the ways to travel on the map, or a train out of King's Cross on the main line
export type RideMode = TravelMode | 'rail';
// metres a second in the ride scene: a brisk walk, a cab and a bus in town traffic, the DLR, the Tube, and a
// main-line train once it is out of the station
export const RIDE_SPEED: Record<RideMode, number> = {
  walk: 1.6,
  cab: 13,
  car: 14,
  bus: 10,
  dlr: 15,
  tube: 18,
  rail: 30,
};
// where the scene is: the street (walking, a cab, a bus), the DLR's viaduct, the Tube's tunnel, or the railway's
// brick viaduct over the terraces
export type RideSetting = 'street' | 'viaduct' | 'tunnel' | 'railway';
export const RIDE_SETTING: Record<RideMode, RideSetting> = {
  walk: 'street',
  cab: 'street',
  car: 'street',
  bus: 'street',
  dlr: 'viaduct',
  tube: 'tunnel',
  rail: 'railway',
};

// the journey's distance and the position at any second: speeding up, a steady run, then slowing to a stop
export function rideProfile(mode: RideMode, seconds: number) {
  const total = Math.max(1, seconds);
  const speed = RIDE_SPEED[mode];
  // a train takes its time to get going and to stop at the platform
  const up = Math.min(mode === 'walk' ? 0.4 : mode === 'rail' ? 4 : 1.2, total / 3);
  const down = Math.min(mode === 'walk' ? 0.4 : mode === 'rail' ? 5 : 1.6, total / 3);
  const distance = speed * (total - up / 2 - down / 2);
  const at = (second: number) => {
    const u = Math.max(0, Math.min(total, second));
    if (u < up) return (0.5 * speed * u * u) / up;
    if (u < total - down) return 0.5 * speed * up + speed * (u - up);
    const left = total - u;
    return distance - (0.5 * speed * left * left) / down;
  };
  const speedAt = (second: number) => {
    const u = Math.max(0, Math.min(total, second));
    if (u < up) return (speed * u) / up;
    if (u < total - down) return speed;
    return (speed * (total - u)) / down;
  };
  return { distance, seconds: total, at, speedAt };
}

type Spot = { area: string; position: [number, number] };
// the areas from one place to another: where you start, the areas of places close to the straight way between
// (in the order you reach them), and where you are going; each named once, in turn
export function areasOnTheWay(from: Spot, to: Spot, places: Spot[], near = 60): string[] {
  const [ax, az] = from.position;
  const dx = to.position[0] - ax;
  const dz = to.position[1] - az;
  const length2 = dx * dx + dz * dz || 1;
  const between = places
    .map((place) => {
      const t = ((place.position[0] - ax) * dx + (place.position[1] - az) * dz) / length2;
      const px = ax + dx * t - place.position[0];
      const pz = az + dz * t - place.position[1];
      return { area: place.area, t, off: Math.hypot(px, pz) };
    })
    .filter((item) => item.t > 0.05 && item.t < 0.95 && item.off <= near)
    .sort((a, b) => a.t - b.t)
    .map((item) => item.area);
  const all = [from.area, ...between, to.area];
  return all.filter((area, i) => area.trim() && all.indexOf(area) === i);
}

// a repeatable sequence of numbers from a seed, so the same journey always passes the same street
export function seeded(seed: number) {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const seedOf = (text: string) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 0x1000193);
  return hash >>> 0;
};
